import { useEffect, useState } from 'react'

import type { Conversation } from './conversations-model'
import type { InteractionAggregateRouteRepository } from './interaction-aggregate-repository'

type ResolvedAggregateState = {
  status: 'idle' | 'loading' | 'ready' | 'unavailable'
  aggregateId: string | null
}

/**
 * The interaction aggregate of the selected conversation.
 *
 * Derived from the current selection on every render: a conversation that names
 * its aggregate resolves at once, and a legacy contact's route result applies
 * only to the contact (and tenant) it was resolved for. The previous selection's
 * aggregate is never returned for a new one, so the readers keyed by it
 * (ownership, SMS route) never start a request for the previous conversation.
 */
export function useResolvedInteractionAggregate(
  repository: InteractionAggregateRouteRepository | undefined,
  tenantId: string,
  conversation: Conversation | null
): ResolvedAggregateState {
  const directAggregateId = conversation?.aggregateId ?? null
  const getLegacyContactRoute = repository?.getLegacyContactRoute
  const legacyPatientId =
    conversation && !directAggregateId && getLegacyContactRoute
      ? (conversation.patientId ?? null)
      : null
  const legacyKey = legacyPatientId ? `${tenantId}:${legacyPatientId}` : null
  // New activity on the contact resolves its route again (it may have gained
  // an aggregate); the last result for the same contact stays meanwhile.
  const activityAtMs = conversation?.timestamp ?? null
  const [legacy, setLegacy] = useState<
    (ResolvedAggregateState & { key: string }) | null
  >(null)

  useEffect(() => {
    if (!legacyKey || !legacyPatientId || !getLegacyContactRoute) return
    let active = true
    void getLegacyContactRoute(tenantId, legacyPatientId)
      .then((route) => {
        if (!active) return
        setLegacy(
          route.legacyPatientId === legacyPatientId
            ? {
                key: legacyKey,
                status: 'ready',
                aggregateId: route.aggregateId,
              }
            : { key: legacyKey, status: 'unavailable', aggregateId: null }
        )
      })
      .catch(() => {
        if (active) {
          setLegacy({
            key: legacyKey,
            status: 'unavailable',
            aggregateId: null,
          })
        }
      })
    return () => {
      active = false
    }
  }, [
    activityAtMs,
    getLegacyContactRoute,
    legacyKey,
    legacyPatientId,
    tenantId,
  ])

  if (!conversation) return { status: 'idle', aggregateId: null }
  if (directAggregateId) {
    return { status: 'ready', aggregateId: directAggregateId }
  }
  if (!legacyKey) return { status: 'unavailable', aggregateId: null }
  if (legacy?.key !== legacyKey) return { status: 'loading', aggregateId: null }
  return { status: legacy.status, aggregateId: legacy.aggregateId }
}
